import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button, Tooltip } from 'antd';

import Alert from 'components/Alert/Alert';
import { getInviteForInterviewButtonTooltip } from 'components/Applications/InterviewView/utils/getInviteForInterviewButtonTooltip';

import {
  selectActiveApplicationDetails,
  selectActiveUserId
} from 'features/applications/applicationsSelectors';
import applicationsService from 'features/applications/applicationsService';
import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';

import useErrorCatch from 'hooks/useErrorCatch';

import { INVITE_FOR_INTERVIEW_ALLOWED_STAGES } from 'constants/applications';
import { AlertType, Dictionaries } from 'constants/enums';
import { TOUR_STEP_TARGET_APPLICATION_INTERVIEW_TAB } from 'constants/tour';

import styles from './RequestInterview.module.scss';

interface RequestInterviewProps {
  onRequestSent: () => void;
  isLoading: boolean;
  onLoading: (isLoading: boolean) => void;
}

const RequestInterview: FC<RequestInterviewProps> = ({
  onRequestSent,
  isLoading,
  onLoading
}) => {
  const { t } = useTranslation();

  const catchError = useErrorCatch();
  const applicationId = useSelector(selectActiveUserId);
  const application = useSelector(selectActiveApplicationDetails);
  const clientApplicationStages = useSelector(
    selectDictionary(Dictionaries.ApplicationStageClient)
  );

  const isInviteForInterviewAllowed =
    application?.clientStage != null &&
    INVITE_FOR_INTERVIEW_ALLOWED_STAGES.includes(application.clientStage);

  const handleSendInterviewRequest = async () => {
    try {
      onLoading(true);
      await applicationsService.sendInterviewRequest(applicationId);
      onRequestSent();
    } catch (error) {
      catchError({ error });
    } finally {
      onLoading(false);
    }
  };

  return (
    <div id={TOUR_STEP_TARGET_APPLICATION_INTERVIEW_TAB}>
      <h2 className={styles.title}>
        {t('sidebar.application.interview.request.title')}
      </h2>
      <Alert type={AlertType.Info} className={styles.alert}>
        {t('sidebar.application.interview.request.readyToInterview', {
          name: application?.jobseekerFullName,
          job: application?.jobName
        })}
      </Alert>
      {t('sidebar.application.interview.request.askToShare')}

      <Tooltip
        title={getInviteForInterviewButtonTooltip(
          isInviteForInterviewAllowed,
          t,
          clientApplicationStages
        )}
      >
        <Button
          type="primary"
          className={styles.btnFullWidth}
          onClick={handleSendInterviewRequest}
          loading={isLoading}
          disabled={!isInviteForInterviewAllowed}
        >
          {t('sidebar.application.interview.request.inviteForInterview')}
        </Button>
      </Tooltip>
    </div>
  );
};

export default RequestInterview;
